/**
 * Upload Resource Access Configuration
 *
 * WASA: Malicious Upload through Response Manipulation
 * - Access is DENIED BY DEFAULT.
 * - Developers MUST declare allowed access for each resource in code.
 * - Server response (rowData) is never trusted for security decisions;
 *   it is validated against this allowlist before allowing upload.
 */

/** Allowed file type keys as returned by API. Only these are permitted. */
export const ALLOWED_UPLOAD_FILE_TYPES = Object.freeze(['PDF', 'Image']);

/** Max file size in MB. Server-sent fileSize is capped by this. */
export const MAX_UPLOAD_FILE_SIZE_MB = 5;

/** Accept attribute per file type (for FileUpload). Declared in code, not from server. */
export const UPLOAD_ACCEPT_BY_TYPE = Object.freeze({
    PDF: '.pdf',
    Image: '.jpg,.jpeg,.png,.JPG,.JPEG,.PNG',
});

/**
 * Document names that are allowed for upload. Any other name is DENIED.
 * When the backend adds a new document type, add its exact name here.
 */
export const ALLOWED_DOCUMENT_NAMES = Object.freeze([
    // Image documents
    'Photograph with white background',
    'Specimen Signature',
    
    // PDF documents
    'Government Employees- Appointment Letter/ ID Card',
    'Self-attested proof of age',
    'Self-attested proof of residence/domicile certificate',
    'Self-attested Internship Certificate',
    'Self-attested copy of BNYS Degree',
    'Self-attested copy of State Board Certificates',
    'Self-attested additional qualification to BNYS Certificate (if applicable)',
    'Self-attested Marriage Certificate/Newspaper Advt/affidavit to declare the name change',
]);

/**
 * Validates server-provided rowData against the allowlist.
 * Use the returned values for accept/maxSize; never use rowData.fileType or rowData.fileSize directly for security.
 *
 * @param {Object} rowData - Resource from API (may be manipulated)
 * @returns {{ allowed: boolean, accept?: string, maxSizeMB?: number, error?: string }}
 */
export function validateUploadResource(rowData) {
    const defaultDeny = { allowed: false, error: 'Access denied. Resource not allowed.' };

    if (!rowData || typeof rowData !== 'object') {
        return defaultDeny;
    }

    const fileType = rowData.fileType;
    const fileSize = Number(rowData.fileSize);
    const documentName = typeof rowData.documentName === 'string' ? rowData.documentName.trim() : '';

    if (!documentName) {
        return { ...defaultDeny, error: 'Invalid document name.' };
    }

    // When updating, id must be a positive integer (prevents response manipulation to target other resources)
    const id = rowData.id;
    const isEditExistingDocument = id != null && id !== '' && Number.isInteger(Number(id)) && Number(id) > 0;
    if (id != null && id !== '') {
        const numId = Number(id);
        if (!Number.isInteger(numId) || numId <= 0) {
            return { ...defaultDeny, error: 'Invalid document id.' };
        }
    }

    // Deny unless document name is in the explicit allowlist (exact match)
    if (!ALLOWED_DOCUMENT_NAMES.includes(documentName)) {
        return { ...defaultDeny, error: 'This document type is not allowed for upload.' };
    }

    // Deny unless file type is explicitly allowed
    const normalizedFileType = typeof fileType === 'string' ? fileType.trim() : '';
    if (!ALLOWED_UPLOAD_FILE_TYPES.includes(normalizedFileType)) {
        return { ...defaultDeny, error: 'Invalid file type for this resource.' };
    }

    // For existing user documents (edit flow), fileSize from API is in bytes or from template;
    // only enforce 1-5 MB for document-list rows (new upload resource definition).
    if (!isEditExistingDocument) {
        if (!Number.isFinite(fileSize) || fileSize <= 0 || fileSize > MAX_UPLOAD_FILE_SIZE_MB) {
            return {
                ...defaultDeny,
                error: `File size must be between 1 and ${MAX_UPLOAD_FILE_SIZE_MB} MB.`,
            };
        }
    }

    const accept = UPLOAD_ACCEPT_BY_TYPE[normalizedFileType];
    if (!accept) {
        return defaultDeny;
    }

    const effectiveMaxSizeMB = isEditExistingDocument
        ? MAX_UPLOAD_FILE_SIZE_MB
        : Math.min(fileSize, MAX_UPLOAD_FILE_SIZE_MB);

    return {
        allowed: true,
        accept,
        maxSizeMB: effectiveMaxSizeMB,
        fileType: normalizedFileType,
    };
}
